import { inject, Injectable } from '@angular/core';

import {
  ScriptingConditionsValidator,
  ScriptingConditionsValidatorConfig
} from '../../classes/scripting/scripting-conditions-validator';
import { FormScriptingActionsService, ScriptingAction } from './form-scripting-actions.service';
import { CollectionFormService } from '../collection-form.service';
import { AuthService } from '../../../../../core/services/auth/auth.service';
import { CollectionForm } from '../../../../../../models/ts/collection-form.model';
import { FormScriptingField } from '../../classes/form-scripting-field';
import { FormScriptingFieldType } from '../../../../../shared/enums/form-scripting-field-type.enum';
import { CollectionFormRow } from '../../../../../../models/ts/collection-form-row.model';
import { CollectionFormAccordion } from '../../../../../../models/ts/collection-form-accordion.model';
import { CollectionFormField } from '../../../../../../models/ts/collection-form-field.model';
import { FormFieldType } from '../../../../../../models/ts/form-field-type.model';
import { EvaluationDto } from '../../../../../../models/ts/evaluation-dto.model';
import { Claims } from '../../../../../core/constants/claims';
import { EvaluationsOnLoadOfForm } from '../../../../../../models/ts/evaluations-on-load-of-form.model';
import { EvaluationActionDto } from '../../../../../../models/ts/evaluation-action-dto.model';
import { Store } from '@ngrx/store';
import { formsActions } from '../../../../../store/features/forms/forms-actions';

/**
 * Service providing functions to perform scripting on a CollectionFormField.
 * @see ScriptingConditionsValidator
 */
@Injectable({
  providedIn: 'root'
})
export class FormScriptingService {

  private authService = inject(AuthService);
  private formScriptingActionsService = inject(FormScriptingActionsService);
  private store$ = inject(Store);

  private actionCount = 0;
  private actionsToExecute = 0;

  /**
   * Returns true if a field has any scripting.
   * @param {CollectionFormField} field
   * @return {boolean}
   */
  public static hasScripting(field: CollectionFormField): boolean {
    return field.Evaluations.length > 0;
  }

  /**
   * Returns true if any field script is set to run on form open.
   * @param {CollectionFormField} field
   * @return {boolean}
   */
  public static hasOnOpenScripting(field: CollectionFormField): boolean {
    return field.Evaluations.findIndex(evaluation => evaluation.OnLoadOfForm == EvaluationsOnLoadOfForm.Yes) != -1;
  }

  /**
   * Gets only the scripts that should be run on form open.
   * @param {CollectionForm} form
   * @return {EvaluationDto[]}
   */
  public static getOnOpenScripts(form: CollectionForm): EvaluationDto[] {
    const fields = CollectionFormService.getFields(form, field => FormScriptingService.hasOnOpenScripting(field));
    return fields.map(field => field.Evaluations).reduce((acc, val) => acc.concat(val), []);
  }

  /**
   * Gets all the scripts of given form.
   * @param {CollectionForm} form
   * @return {EvaluationDto[]}
   */
  public static getFormScripts(form: CollectionForm): EvaluationDto[] {
    const fields = CollectionFormService.getFields(form, field => FormScriptingService.hasScripting(field));
    return [...new Set(fields.map(field => field.Evaluations).reduce((acc, val) => acc.concat(val), []))];
  }

  /**
   * Gets a field from a CollectionForm as a FormScriptingField.
   * @param data
   * @param fieldID
   * @param viewDataSourcesId
   */
  public static getFormScriptingField(data: CollectionForm, fieldId: number, viewDataSourcesId: number): FormScriptingField | undefined {
    for (const accordion of data.Accordions) {
      for (const row of accordion.Rows) {
        for (const container of row.Containers) {
          const field = container.Fields.find(field =>
            FormScriptingService.isScriptingField(field, fieldId, viewDataSourcesId) ||
            FormScriptingService.isScriptingGrid(field, viewDataSourcesId)
          );
          if (field) {
            return {
              field: field,
              accordion: accordion,
              row: row,
              type: field.IsGridField ? FormScriptingFieldType.Grid : FormScriptingFieldType.Field
            };
          }
        }
      }
    }
    return undefined;
  }

  /**
   * Function to check if a field is a scripting grid.
   * @param {CollectionFormField} field
   * @param {number} vdsId
   * @return {boolean}
   * @private
   */
  private static isScriptingGrid(field: CollectionFormField, vdsId: number): boolean {
    return (field.DataDesignViewDataSourcesID == vdsId && FormScriptingService.scriptingGridFormFieldTypes.includes(field.FormFieldType));
  }

  private static scriptingGridFormFieldTypes = [FormFieldType.LinkedCollection, FormFieldType.List, FormFieldType.ChkQuestions];

  /**
   * Function to check if a field is a scripting field.
   * @param {CollectionFormField} field
   * @param {number} fieldId
   * @param {number} vdsId
   * @return {boolean}
   * @private
   */
  private static isScriptingField(field: CollectionFormField, fieldId: number, vdsId: number): boolean {
    return (field.CollectionFieldsID == fieldId || (field.SourceCollectionFieldsID > 0 && field.SourceCollectionFieldsID == fieldId)) && (vdsId == -1 || field.DataDesignViewDataSourcesID == vdsId);
  }

  /**
   * Returns a boolean indicating if a row should be hidden based on hidden fields and any present elements like title bars.
   * @param row
   */
  public static hideCompleteRow(row: CollectionFormRow): boolean {
    const fields = CollectionFormService.extractFieldsFromRow(row);
    // row is hidden if no visible field is found AND no elements are present (titlebars, etc. shouldn't be hidden due to no fields being present on the row)
    return fields.findIndex(f => !FormScriptingService.isHiddenField(f)) == -1 && !FormScriptingService.rowContainsElements(row);
  }

  /**
   * Hides an accordion if all fields in the accordion are hidden.
   * @param accordion
   */
  public static hideCompleteAccordion(accordion: CollectionFormAccordion): boolean {
    const fields = CollectionFormService.extractFieldsFromAccordion(accordion);
    return fields.findIndex(f => !FormScriptingService.isHiddenField(f)) == -1;
  }

  /**
   * Checks if a field is hidden.
   * @param field
   */
  private static isHiddenField(field: CollectionFormField): boolean {
    return field.IsHidden == true && !FormScriptingService.isFormElement(field);
  }

  private static isFormElement(field: CollectionFormField): boolean {
    return field.FormFieldType == FormFieldType.TitleBar
      || field.FormFieldType == FormFieldType.EmptyField
      || field.FormFieldType == FormFieldType.Text
      || field.FormFieldType == FormFieldType.Button
      || field.FormFieldType == FormFieldType.ImageComponent
      || field.FormFieldType == FormFieldType.MailingList;
  }

  private static rowContainsElements(row: CollectionFormRow): boolean {
    return row.Containers.flatMap(c => c.Fields).findIndex(f => FormScriptingService.isFormElement(f)) != -1;
  }

  /**
   * Performs only the onOpen scripting on a form. (Fields with scripts where OnLoadOfForm = Yes)
   * @param {string} formId
   * @param {CollectionForm} form
   */
  public performOnOpenScripting(formId: string, form: CollectionForm): void {
    //console.debug('Performing onOpen scripting');
    this.performScripting(formId, form, FormScriptingService.getOnOpenScripts(form));
  }

  /**
   * Performs the scripting(Evaluations) on a form and returns the updated form.
   * Loops through all the scripting and performs the actions according to the conditions being OK or not.
   * @param formId
   * @param form
   * @param scripting
   */
  public performScripting(formId: string, form: CollectionForm, scripting: EvaluationDto[]): void {
    if (scripting.length > 0) {
      const conditionsValidator = new ScriptingConditionsValidator();
      const userId = this.authService.getAccessTokenClaim(Claims.UserID) as number;
      let actions: EvaluationActionDto[] = []

      // Evaluate all the conditions and get the actions to perform
      scripting.forEach((evaluation, index) => {
        conditionsValidator.configuration = new ScriptingConditionsValidatorConfig(form, evaluation.Conditions, userId);
        const isValidCondition = conditionsValidator.validateConditions();
        actions = actions.concat(isValidCondition ? evaluation.Actions.ActionsWhenOk : evaluation.Actions.ActionsWhenNotOk);
      });

      if(actions.length == 0) {
        this.store$.dispatch(formsActions.scriptingComplete({ formId }));
      } else {
        this.store$.dispatch(formsActions.scriptingExecutingActions({ formId, actions }));
      }
    } else {
      this.store$.dispatch(formsActions.scriptingComplete({ formId }));
    }
  }

  public executeActions(formId: string, form: CollectionForm, actions: EvaluationActionDto[]): void {
    // Perform all actions
    let scriptingActions: ScriptingAction[] = []
    actions.forEach(action => {
      const scriptingField = FormScriptingService.getFormScriptingField(form, action.FieldsID, action.ViewDataSourcesID);
      if (scriptingField) {
        scriptingActions = scriptingActions.concat(FormScriptingActionsService.getScriptingFieldActions(formId, form, scriptingField, action));
      } else {
        console.error('Field \'' + action.Caption + '\' not present on the form so could not perform the action');
      }
    });
    this.actionCount = 0;
    this.actionsToExecute = scriptingActions.length;
    //console.debug(`Performing ${this.actionsToExecute} actions`);
    let updatedForm = structuredClone(form);
    scriptingActions.forEach(action => {
      //console.debug(`Performing action: Set ${action.property} to ${action.value} on field ${action.fieldId}`);
      updatedForm = CollectionFormService.setFieldPropertyValue(
        updatedForm,
        action.fieldId,
        action.property,
        action.value
      );
      this.bumpCounter(formId);
    });
    this.store$.dispatch(
      formsActions.updateFormData({
        formId,
        data: updatedForm
      })
    );
  }

  public bumpCounter(formId: string): void {
    this.actionCount++;
    //console.debug(`Executed action ${this.actionCount} out of ${this.actionsToExecute}`);
    if (this.actionCount == this.actionsToExecute) {
      //console.debug('All actions executed');
      this.store$.dispatch(formsActions.scriptingComplete({ formId }));
      this.actionCount = 0;
      this.actionsToExecute = 0;
    }
  }

}
